import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  useInvoices,
  useAddInvoice,
  useUpdateInvoice,
} from "../../../api/lists";
import type {
  InvoiceEntity,
  InvoiceId,
  NewInvoiceEntity,
  UpdateInvoiceEntity,
} from "../../../api/types";
import {
  AdminListBody,
  AdminListPageHeader,
} from "../../../components/AdminList";
import {
  ViewModeToggle,
  type ViewMode,
} from "../../../components/ViewModeToggle";
import {
  SortByDropdown,
  type SortByOption,
} from "../../../components/SortByDropdown";
import { useRegistry } from "../../../context/RegistryContext";
import {
  invoiceSender,
  senderKey,
  senderName,
  type InvoiceSender,
} from "../../../logic/invoice-sender";
import { InvoiceFormDialog } from "../../../components/Settings/InvoiceFormDialog";
import { INVOICE_PARAM } from "../../../logic/invoice-path";
import { InvoiceSummaryModal } from "../../../components/Settings/InvoiceSummaryModal";
import { InvoiceTransactionsModal } from "../../../components/Settings/InvoiceTransactionsModal";
import { defaultFilters, type Filters, type InvoiceSort } from "./types";
import { filterInvoices, sortInvoices } from "./filterInvoices";
import { InvoiceFilters } from "./components/InvoiceFilters";
import { InvoiceListView } from "./components/InvoiceListView";
import { InvoiceTileView } from "./components/InvoiceTileView";

const VIEW_MODE_KEY = "invoices.viewMode";
const SORT_BY_KEY = "invoices.sortBy";

const SORT_OPTIONS: ReadonlyArray<SortByOption<InvoiceSort>> = [
  { value: "dueDateDesc", label: "Due date" },
  { value: "receivedDateDesc", label: "Date received" },
  { value: "nameAsc", label: "Name" },
];

const readStoredViewMode = (): ViewMode => {
  try {
    const stored = window.localStorage?.getItem?.(VIEW_MODE_KEY);
    return stored === "list" ? "list" : "tile";
  } catch {
    return "tile";
  }
};

const writeStoredViewMode = (next: ViewMode) => {
  try {
    window.localStorage?.setItem?.(VIEW_MODE_KEY, next);
  } catch {
    // ignore — SSR, private mode, or test env without storage
  }
};

const isInvoiceSort = (s: string | null): s is InvoiceSort =>
  s === "dueDateDesc" || s === "receivedDateDesc" || s === "nameAsc";

const readStoredSortBy = (): InvoiceSort => {
  try {
    const stored = window.localStorage?.getItem?.(SORT_BY_KEY);
    return isInvoiceSort(stored) ? stored : "dueDateDesc";
  } catch {
    return "dueDateDesc";
  }
};

const writeStoredSortBy = (next: InvoiceSort) => {
  try {
    window.localStorage?.setItem?.(SORT_BY_KEY, next);
  } catch {
    // ignore
  }
};

export const InvoicesPage = () => {
  const { data: invoices, isPending } = useInvoices();
  const addInvoice = useAddInvoice();
  const updateInvoice = useUpdateInvoice();

  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InvoiceEntity | null>(null);

  // The open invoice view lives in the URL so another page can deep-link to
  // an invoice (a transaction's Invoice row, the duplicate dialog's "Open
  // existing"), and so it survives a reload — and there is no state to sync
  // when the link changes underfoot.
  const [searchParams, setSearchParams] = useSearchParams();
  const viewingId = searchParams.get(INVOICE_PARAM) as InvoiceId | null;
  const setViewingId = (id: InvoiceId | null) =>
    setSearchParams(
      (prev) => {
        if (id === null) prev.delete(INVOICE_PARAM);
        else prev.set(INVOICE_PARAM, id);
        return prev;
      },
      { replace: true },
    );
  const [transactionsFor, setTransactionsFor] = useState<InvoiceEntity | null>(
    null,
  );

  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    readStoredViewMode(),
  );
  const [sortBy, setSortBy] = useState<InvoiceSort>(() => readStoredSortBy());

  const handleViewModeChange = (next: ViewMode) => {
    setViewMode(next);
    writeStoredViewMode(next);
  };

  const handleSortByChange = (next: InvoiceSort) => {
    setSortBy(next);
    writeStoredSortBy(next);
  };

  // One chip per sender that has an invoice, payees and accounts together,
  // alphabetical so a payee doesn't hide among the accounts.
  // Hidden senders included: their invoices are still listed, so they still
  // need a chip with a name.
  const { withHidden } = useRegistry();
  const senderOptions = useMemo<InvoiceSender[]>(() => {
    if (!invoices) return [];
    const seen = new Map<string, InvoiceSender>();
    for (const item of invoices) {
      const sender = invoiceSender(item, withHidden);
      if (sender) seen.set(senderKey(sender), sender);
    }
    return [...seen.values()].sort((a, b) =>
      senderName(a).localeCompare(senderName(b)),
    );
  }, [invoices, withHidden]);

  const sortedAndFiltered = useMemo(() => {
    if (!invoices) return [];
    return sortInvoices(filterInvoices(invoices, filters), sortBy);
  }, [invoices, filters, sortBy]);

  const total = invoices?.length ?? 0;

  const handleAdd = () => {
    setEditingItem(null);
    setFormOpen(true);
  };

  const handleEdit = (item: InvoiceEntity) => {
    setEditingItem(item);
    setFormOpen(true);
  };

  // Throws on failure: the form shows the error itself, and turns a duplicate
  // refusal into its open / discard / create-anyway dialog.
  const handleSave = async (
    entity: NewInvoiceEntity,
    { force }: { force: boolean },
  ) => {
    if (editingItem) {
      await updateInvoice.mutateAsync({
        id: editingItem.id,
        ...entity,
      } as UpdateInvoiceEntity);
    } else {
      await addInvoice.mutateAsync({ invoice: entity, force });
    }
    setFormOpen(false);
  };

  return (
    <Box sx={{ p: 2 }}>
      <AdminListPageHeader
        title="Invoices"
        actions={
          <>
            <SortByDropdown
              value={sortBy}
              onChange={handleSortByChange}
              options={SORT_OPTIONS}
              labelId="invoice-sort-by"
            />
            <ViewModeToggle
              value={viewMode}
              onChange={handleViewModeChange}
              ariaLabel="invoices view mode"
            />
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={handleAdd}
            >
              New invoice
            </Button>
          </>
        }
      />

      <InvoiceFilters
        value={filters}
        onChange={setFilters}
        senderOptions={senderOptions}
      />

      <AdminListBody
        isLoading={isPending}
        total={total}
        filteredCount={sortedAndFiltered.length}
        noun="invoice"
      >
        {viewMode === "list" ? (
          <InvoiceListView
            invoices={sortedAndFiltered}
            onEdit={handleEdit}
            onView={(item) => setViewingId(item.id)}
            onViewTransactions={setTransactionsFor}
          />
        ) : (
          <InvoiceTileView
            invoices={sortedAndFiltered}
            sortBy={sortBy}
            onEdit={handleEdit}
            onView={(item) => setViewingId(item.id)}
            onViewTransactions={setTransactionsFor}
          />
        )}
      </AdminListBody>

      {viewingId && (
        <InvoiceSummaryModal
          invoiceId={viewingId}
          open
          onClose={() => setViewingId(null)}
          onEdit={handleEdit}
        />
      )}
      {/* After the view, so editing from it opens the form on top of it; the
          view stays open underneath and shows the saved invoice. */}
      {formOpen && (
        <InvoiceFormDialog
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSave={handleSave}
          initialData={editingItem}
          title={editingItem ? "Edit Invoice" : "Add Invoice"}
        />
      )}
      {transactionsFor && (
        <InvoiceTransactionsModal
          invoiceId={transactionsFor.id}
          open
          onClose={() => setTransactionsFor(null)}
        />
      )}
    </Box>
  );
};
